import { AgentTopbar, useAgentOpen } from '@ordo/agent-embed'
import { isAppAdmin } from '@ordo/auth'
import { useSidebarState } from '@ordo/ui/app-shell'
import { AppSidebarDrawer } from '@ordo/ui/app-sidebar'
import { AppTopBar, AppTopBarRight } from '@ordo/ui/app-topbar'
import { MobileFloatingActions } from '@ordo/ui/mobile-floating-actions'
import { MobileTopRow, SHELL_CIRCLE_CLASS } from '@ordo/ui/mobile-top-row'

import { useBoomboxAgentConfig } from '../lib/agent-config'
import type { User } from '../types/users'
import { NotificationBell } from './NotificationBell'
import { Sidebar } from './Sidebar'

async function fetchAgentToken(): Promise<string | null> {
  try {
    const res = await fetch('/api/embed/agent-token', { credentials: 'include' })
    if (!res.ok) {
      return null
    }
    const json = (await res.json()) as { token?: string }
    return json.token ?? null
  } catch {
    return null
  }
}

interface TopBarProps {
  user: User
}

/**
 * Comms' chrome inside the unified `AppShell`: the full-width desktop top bar
 * (app row in the corner, the unified inbox bell and search), and on phones
 * the floating top row (menu, app switcher, the same bell) plus the
 * bottom-right search circle. The menu drawer binds to
 * the shell's sidebar state, which closes it on navigation.
 */
export function TopBar({ user }: TopBarProps) {
  const sidebar = useSidebarState()
  const openSearch = useAgentOpen()
  const agentConfig = useBoomboxAgentConfig({ isAdmin: isAppAdmin(user, 'comms') })

  return (
    <>
      <AppTopBar size="sm">
        <AgentTopbar
          hostApp="boombox"
          config={agentConfig}
          apiBaseUrl={import.meta.env['VITE_AGENT_API_URL'] ?? 'https://agent.ordo.com'}
          getAgentToken={fetchAgentToken}
        />
        <AppTopBarRight>
          <NotificationBell />
        </AppTopBarRight>
      </AppTopBar>
      <MobileTopRow currentAppId="comms" trailing={<NotificationBell triggerClassName={SHELL_CIRCLE_CLASS} />} />
      <MobileFloatingActions onSearch={openSearch} />
      <AppSidebarDrawer title="Comms navigation">
        <Sidebar user={user} variant="drawer" onClose={() => sidebar?.setOpen(false)} />
      </AppSidebarDrawer>
    </>
  )
}
